import {type MapIdToPathFn, type UrlRule} from "../types.ts";
import {getMapIdToPath} from "../url-builder-factory.ts";

import {mapParam} from "./map-param.ts";

/**
 * A map of path IDs to paths.  This map is used as a default for apps that
 * don't specify their own map using `configureUrlConstructionRules`.
 */
const AUTH_PATH_MAP = {
    signup: "/signup",
    logout: "/logout",
    login: "/login",
} as const;

/**
 * Remove the continue param if the URL is an auth URL.
 *
 * This looks at the value of the given param, and if it is not present,
 * it will set the value of the param in the URL being constructed to the
 * given default value.
 *
 * This is useful for ensuring that required params have a value.
 *
 * @param paramName The name of the continue param. Defaults to "continue".
 * @param mapIdToPath This is used in conjuection with AUTH_PATH_IDS to
 * determine whether the continue param is an auth URL for the current app.
 * We default to `getMapIdToPath()` which returns the mapping function for
 * the current app. In tests we explicitly pass a mock function.
 * @returns A URL rule that removes the continue param if the URL is
 * an auth URL.
 */
export const noContinueForAuthUrls = (
    paramName: string = "continue",
    mapIdToPath: MapIdToPathFn = getMapIdToPath(),
): UrlRule =>
    mapParam(paramName, (v) => {
        // If we're already continuing to a auth-related page, or the homepage,
        // we just ignore the result and don't add a continue path.
        if (v) {
            const url = new URL(v, location.origin);

            for (const pathId of Object.keys(AUTH_PATH_MAP)) {
                if (url.pathname === mapIdToPath(pathId)) {
                    return null;
                }
            }

            for (const pathname of Object.values(AUTH_PATH_MAP)) {
                if (url.pathname === pathname) {
                    return null;
                }
            }

            // We check that the whole continue URL is just the root path to
            // match existing behaviour.
            if (v === "/") {
                return null;
            }
        }
        return v;
    });
